import React, { Component } from "react";
import { inject, observer } from "mobx-react";
import { withStyles, Theme } from "@material-ui/core";
import { RouteComponentProps } from "react-router-dom";
import classNames from "classnames";
import { InjectedNotistackProps, withSnackbar } from "notistack";
import CircularProgress from "@material-ui/core/CircularProgress/CircularProgress";
import { COLORS } from "../../styles/colors";
import { CheckboxWithLabel, TextField } from "formik-material-ui";
import { Formik, FormikActions, Form, Field } from "formik";
import createStyles from "@material-ui/core/styles/createStyles";
import { WithTranslation, withTranslation } from "react-i18next";
import * as Yup from "yup";
import {
  EmailValidator,
  PhoneValidator,
  StringValidator
} from "../../utils/validators";
import Card from "@material-ui/core/Card";
import CardContent from "@material-ui/core/CardContent";
import { DialogComponent } from "../DialogComponent";
import UserStore, { User } from "../../stores/userStore";
import Divider from "@material-ui/core/Divider";
import Typography from "@material-ui/core/Typography";
import CardActions from "@material-ui/core/CardActions";
import Button from "@material-ui/core/Button";
import { AutocompleteMultiple } from "../AutocompleteNew";
import ProjectStore from "../../stores/projectStore";
import BackIcon from "@material-ui/icons/ArrowBackIos";
import {dataToUpperCase} from "../../utils/dataToUppercase"

interface MatchParams {
  id: string;
}

interface UserProps extends WithTranslation, InjectedNotistackProps {}

interface InjectedProps extends UserProps, RouteComponentProps<MatchParams> {
  userStore: UserStore;
  projectStore: ProjectStore;
  classes: any;
  userId: string;
}

class UserForm {
  constructor(user?: User) {
    if (user) {
      this.firstName = user.firstName;
      this.lastName = user.lastName;
      this.email = user.email;
      this.phone = user.phone;
      this.active = user.active.toString();
      this.projects = user.projects.map(item => item.id);
      this.extendedMeasurement = user.extendedMeasurement;
    }
  }
  firstName: string = "";
  lastName: string = "";
  email: string = "";
  phone: string = "";
  active: string = "false";
  projects: string[] = [];
  extendedMeasurement: boolean = false;
}

const UserSchema = Yup.object().shape<UserForm>({
  firstName: StringValidator,
  lastName: StringValidator,
  email: EmailValidator,
  phone: PhoneValidator,
  active: Yup.string(),
  projects: Yup.array()
    .of(Yup.string())
    .required(),
  extendedMeasurement: Yup.boolean()
});

interface UserState {
  edit: boolean;
  open: boolean;
  add: boolean;
}

@inject("userStore", "projectStore")
@observer
class UserDetails extends Component<UserProps, UserState> {
  get p() {
    return this.props as InjectedProps;
  }

  state = {
    edit: !this.p.match.params.id,
    open: false,
    add: false
  };

  componentDidMount(): void {
    this.getUserDetails();
    this.setState({ add: !this.state.edit });
  }

  componentWillUnmount(): void {
    this.p.userStore.resetUser();
  }

  submit = async (values: UserForm) => {
    dataToUpperCase(values);
    if (this.p.match.params.id) {
      this.editUser(values);
    } else {
      this.addUser(values);
    }
  };

  getUserDetails = async () => {
    if (this.p.match.params.id) {
      const success = await this.p.userStore.getUserByIdAction(
        this.p.match.params.id
      );
      if (!success) {
        this.p.enqueueSnackbar(this.p.t("ERRORS.CANT_GET_USER"), {
          variant: "error"
        });
      }
    }
  };

  addUser = async (values: UserForm) => {
    const success = await this.p.userStore.addUserAction(values);
    if (!success) {
      this.p.enqueueSnackbar(this.p.t("ERRORS.CANT_ADD_USER"), {
        variant: "error"
      });
    } else {
      this.p.enqueueSnackbar(this.p.t("USER.ADD_SUCCESS"), {
        variant: "success"
      });
      this.p.history.replace("/users");
    }
  };

  editUser = async (values: UserForm) => {
    const id = this.p.match.params.id;
    const success = await this.p.userStore.editUserAction(values, id);

    if (!success) {
      this.props.enqueueSnackbar(this.p.t("ERRORS.CANT_EDIT_USER"), {
        variant: "error"
      });
    } else {
      this.props.enqueueSnackbar(this.p.t("USER.UPDATE_SUCCESS"), {
        variant: "success"
      });
      this.p.history.replace("/users");
    }
  };

  getProjectOptions = async (search: string) => {
    await this.p.projectStore.getProjectOptionsAutocompleteAction({
      name: search
    });
  };

  enableEdit = (
    resetForm: (values: any) => void,
    values: any,
    e: React.MouseEvent<HTMLButtonElement>
  ) => {
    e.preventDefault();
    resetForm(values);
    this.setState({ edit: true });
  };

  disableEdit = () => {
    this.setState({ edit: false });
  };

  handleCloseDialog = () => {
    this.setState({ open: false });
  };

  handleClickOpenDialog = () => {
    this.setState({ open: true });
  };

  deleteUser = async () => {
    const id = this.p.match.params.id;
    const success = await this.p.userStore.deleteUserAction(id);

    if (!success) {
      this.props.enqueueSnackbar(this.p.t("ERRORS.CANT_DELETE_USER"), {
        variant: "error"
      });
    } else {
      this.props.enqueueSnackbar(this.p.t("USER.DELETE_SUCCESS"), {
        variant: "success"
      });
      this.p.history.replace("/users");
    }
  };

  backToList = () => {
    this.p.history.goBack();
  };

  render() {
    const {
      classes,
      t,
      userStore: { pending, user },
      projectStore: { projectOptionsAutocomplete }
    } = this.p;
    const userId = this.p.match.params.id;
    return (
      <Formik
        enableReinitialize
        initialValues={new UserForm(user)}
        validationSchema={UserSchema}
        onSubmit={(values: UserForm, actions: FormikActions<UserForm>) => {
          this.submit(values).then(() => {
            actions.setSubmitting(false);
          });
        }}
        onReset={values => {
          values = new UserForm();
        }}
      >
        {props => {
          const { setFieldValue, setFieldTouched, errors, touched } = props;
          return (
            <Form noValidate>
              <div className={"titleContainer"}>
                <BackIcon className={"backList"} onClick={this.backToList} />
                <Typography
                  className={classNames(classes.dividerFullWidth, "title")}
                  display="block"
                  variant="subtitle1"
                >
                  {t("USER.TITLE")}
                </Typography>
              </div>
              <Divider component="div" className={"divider"} />
              <div className={classes.container}>
                <div className={classes.content}>
                  <Card
                    className={classNames(classes.card, classes.cardContainer)}
                  >
                    <CardContent className={classes.cardContent}>
                      <Field
                        id="firstName"
                        name="firstName"
                        disabled={!this.state.edit}
                        label={t("USER.NAME")}
                        required
                        InputProps={{
                          classes: {
                            notchedOutline: classes.notchedOutline,
                            root: classes.formControl,
                            input: classes.transformText
                          }
                        }}
                        component={TextField}
                        className={classes.textField}
                        margin="normal"
                        autoComplete="off"
                        variant="outlined"
                      />
                      <Field
                        id="lastName"
                        name="lastName"
                        disabled={!this.state.edit}
                        label={t("USER.SURNAME")}
                        required
                        InputProps={{
                          classes: {
                            notchedOutline: classes.notchedOutline,
                            root: classes.formControl,
                            input: classes.transformText
                          }
                        }}
                        component={TextField}
                        className={classes.textField}
                        margin="normal"
                        autoComplete="off"
                        variant="outlined"
                      />
                      <Field
                        id="email"
                        name="email"
                        disabled={!this.state.edit}
                        label={t("USER.EMAIL")}
                        required
                        InputProps={{
                          classes: {
                            notchedOutline: classes.notchedOutline,
                            root: classes.formControl
                          }
                        }}
                        component={TextField}
                        className={classes.textField}
                        margin="normal"
                        autoComplete="off"
                        variant="outlined"
                      />
                      <Field
                        id="phone"
                        name="phone"
                        disabled={!this.state.edit}
                        label={t("USER.PHONE_NUMBER")}
                        required
                        InputProps={{
                          classes: {
                            notchedOutline: classes.notchedOutline,
                            root: classes.formControl
                          }
                        }}
                        component={TextField}
                        className={classes.textField}
                        margin="normal"
                        autoComplete="off"
                        variant="outlined"
                      />
                      <AutocompleteMultiple
                        error={errors.projects}
                        touched={touched.projects}
                        label={t("USER.PROJECT")}
                        options={projectOptionsAutocomplete}
                        disabled={!this.state.edit}
                        onInputChange={this.getProjectOptions}
                        onValueChange={(value: any) => {
                          setFieldTouched("projects");
                          setFieldValue("projects", value);
                        }}
                        id="projects"
                        name="projects"
                        required
                        initialValue={user.projects.map((item: any) => ({
                          label: item.name,
                          value: item.id
                        }))}
                      />
                      <Field
                        name="extendedMeasurement"
                        disabled={!this.state.edit}
                        Label={{ label: t("USER.EXTENDED_MEASUREMENT") }}
                        component={CheckboxWithLabel}
                      />
                    </CardContent>
                    <CardActions className={classes.cardAction}>
                      {userId && (
                        <>
                          {!this.state.edit ? (
                            <>
                              <Button
                                aria-label="Edit"
                                type="button"
                                className={classNames(
                                  classes.button,
                                  classes.btn
                                )}
                                onClick={event =>
                                  this.enableEdit(
                                    props.resetForm,
                                    props.values,
                                    event
                                  )
                                }
                                disabled={pending}
                              >
                                {pending ? (
                                  <CircularProgress
                                    size={20}
                                    thickness={5}
                                    color="primary"
                                  />
                                ) : (
                                  t("EDIT")
                                )}
                              </Button>
                              <Button
                                aria-label="Delete"
                                className={classNames(
                                  classes.button,
                                  classes.btn
                                )}
                                onClick={this.handleClickOpenDialog}
                              >
                                {pending ? (
                                  <CircularProgress
                                    size={20}
                                    thickness={5}
                                    color="primary"
                                  />
                                ) : (
                                  t("DELETE")
                                )}
                              </Button>
                            </>
                          ) : (
                            <>
                              <Button
                                aria-label="Save"
                                className={classNames(
                                  classes.button,
                                  classes.btn
                                )}
                                type="submit"
                                disabled={pending}
                              >
                                {pending ? (
                                  <CircularProgress
                                    size={20}
                                    thickness={5}
                                    color="primary"
                                  />
                                ) : (
                                  t("SAVE")
                                )}
                              </Button>
                              <Button
                                aria-label="Delete"
                                className={classNames(
                                  classes.button,
                                  classes.btn
                                )}
                                onClick={this.disableEdit}
                              >
                                {pending ? (
                                  <CircularProgress
                                    size={20}
                                    thickness={5}
                                    color="primary"
                                  />
                                ) : (
                                  t("CANCEL")
                                )}
                              </Button>
                            </>
                          )}
                          <DialogComponent
                            title={"USER.USER_DELETE"}
                            t={t}
                            classes={classes}
                            submitButtonTitle={"DELETE"}
                            cancel={"CANCEL"}
                            pending={pending}
                            handleClickCloseDialog={this.handleCloseDialog}
                            handleDelete={this.deleteUser}
                            open={this.state.open}
                          />
                        </>
                      )}
                      {!this.p.match.params.id && (
                        <Button
                          aria-label="Save"
                          className={classNames(classes.fab, classes.btn)}
                          type="submit"
                        >
                          {pending ? (
                            <CircularProgress
                              size={20}
                              thickness={5}
                              color="primary"
                            />
                          ) : (
                            t("SAVE")
                          )}
                        </Button>
                      )}
                    </CardActions>
                  </Card>
                </div>
              </div>
            </Form>
          );
        }}
      </Formik>
    );
  }
}

const styles = (theme: Theme) =>
  createStyles({
    container: {
      display: "flex",
      justifyContent: "space-evenly",
      width: "100%",
      backgroundSize: "cover"
    },

    transformText: {
      textTransform: "uppercase"
    },

    textField: {
      marginLeft: theme.spacing(1),
      marginRight: theme.spacing(1),
      width: "100%",
      maxWidth: 380,
      marginTop: 12
    },
    customSelect: {
      height: 56
    },
    select: {
      width: "100%",
      maxWidth: 380,
      height: 56,
      margin: theme.spacing(1),
      marginTop: 12,
      borderRadius: 5,
      borderColor: COLORS.WHITE,
      background: "rgba(255, 255, 255, 0.12)"
    },
    notchedOutline: {
      borderWidth: 1,
      borderColor: COLORS.WHITE + "!important"
    },

    formControl: {
      background: "rgba(255, 255, 255, 0.12)"
    },

    content: {
      width: "100%",
      padding: 20,

      "@media (max-width:1200px)": {
        width: "100%",
        display: "flex",
        justifyContent: "center"
      }
    },

    cardContainer: {
      background: "transparent",
      display: "flex",
      flexDirection: "column",
      width: "80%",
      margin: "0 auto",
      overflow: "visible",
      boxShadow: "none"
    },

    cardContent: {
      display: "flex",
      flexDirection: "column",
      alignItems: "center"
    },
    btn: {
      margin: "0 10px",
      width: "10%",
      background: COLORS.PRIMARY_DARK,
      color: COLORS.WHITE
    },

    cardAction: {
      justifyContent: "center"
    },

    btnColor: {
      color: COLORS.WHITE,
      "&:hover": {
        background: COLORS.PRIMARY_DARK
      }
    }
  });

export default withStyles(styles)(withTranslation()(withSnackbar(UserDetails)));
